import { formatCellValue } from "./formatters";

export const copyTableToClipboard = async ({
  columns,
  rows,
  currencySymbol = "$",
  getCellValue,
  getCellStyle,
  hideSubHeaderRow
}) => {
  try {
    // Build HTML table with styling
    let html = '<table style="border-collapse: collapse; font-family: Arial, sans-serif;">';
    
    // Always include main headers row
    html += '<tr>';
    columns.forEach((col) => {
      const headerBgColor = col.headerBgColor || "#f5f5f5";
      const headerTextColor = col.headerTextColor || "#000000";
      const colspan = col.subColumns.length;
      const style = `background-color: ${headerBgColor}; color: ${headerTextColor}; text-align: ${col.align || "center"}; padding: 4px 8px; border: 1px solid #e0e0e0; font-weight: 600;`;
      html += `<th colspan="${colspan}" style="${style}">${col.label}</th>`;
    });
    html += '</tr>';

    // Sub headers row - only include when not hidden
    if (!hideSubHeaderRow) {
      html += '<tr>';
      columns.forEach((col) => {
        col.subColumns.forEach((subCol) => {
          const subHeaderBgColor = subCol.headerBgColor || col.headerBgColor || "#f5f5f5";
          const subHeaderTextColor = subCol.headerTextColor || col.headerTextColor || "#000000";
          const style = `background-color: ${subHeaderBgColor}; color: ${subHeaderTextColor}; text-align: ${subCol.align || "center"}; padding: 4px 8px; border: 1px solid #e0e0e0; font-weight: 600;`;
          html += `<th style="${style}">${subCol.label}</th>`;
        });
      });
      html += '</tr>';
    }
    
    // Add data rows
    rows.forEach((row, rowIndex) => {
      html += '<tr>';
      columns.forEach((col) => {
        col.subColumns.forEach((subCol) => {
          if (subCol.field) {
            // Match the exact same logic as TableCell component
            const rawValue = getCellValue ? getCellValue(row, subCol.field, rowIndex) : row[subCol.field];
            
            // If getCellValue was provided and returned a string, ALWAYS use it directly to preserve spaces
            // This ensures we match exactly what's displayed in the UI
            let displayValue;
            if (getCellValue && typeof rawValue === "string") {
              // getCellValue returned a string - use it directly to preserve ALL spaces
              displayValue = rawValue;
            } else if (getCellValue && rawValue !== String(row[subCol.field])) {
              // getCellValue formatted it differently - use it directly
              displayValue = rawValue;
            } else {
              displayValue = subCol.format
                ? subCol.format(rawValue)
                : formatCellValue(rawValue, subCol.type, currencySymbol);
            }
            
            // Remove HTML tags if any and escape HTML entities, but preserve ALL whitespace
            // Don't use String() conversion as it might normalize spaces
            let plainText = typeof displayValue === "string" 
              ? displayValue.replace(/<[^>]*>/g, "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
              : String(displayValue);
            
            // Get cell alignment
            const dataAlign = subCol.dataAlign !== undefined ? subCol.dataAlign : (subCol.align || "right");
            const cellStyle = getCellStyle ? getCellStyle(row, subCol.field) : {};
            const bgColor = cellStyle.backgroundColor || (rowIndex % 2 === 0 ? "#ffffff" : "#f5f5f5");
            // Use white-space: pre-wrap to preserve ALL spaces including multiple consecutive spaces
            // This ensures spaces are preserved when pasting into applications
            const style = `background-color: ${bgColor}; text-align: ${dataAlign}; padding: 4px 8px; border: 1px solid #e0e0e0; white-space: pre-wrap;`;
            
            // For HTML: Replace multiple consecutive spaces with non-breaking spaces to prevent HTML from collapsing them
            // This preserves the visual appearance when pasting HTML, while plain text version keeps original spaces
            const htmlText = plainText.replace(/ {2,}/g, (spaces) => {
              // Replace each space in a sequence of 2+ spaces with non-breaking space
              // This ensures HTML doesn't collapse multiple spaces
              return spaces.replace(/ /g, '\u00A0');
            });
            
            html += `<td style="${style}">${htmlText}</td>`;
          } else {
            html += '<td style="padding: 4px 8px; border: 1px solid #e0e0e0;"></td>';
          }
        });
      });
      html += '</tr>';
    });
    
    html += '</table>';
    
    // Build plain text version preserving whitespace
    const buildPlainText = () => {
      const headerRows = [];
      // Always include main headers
      const mainHeaderRow = columns.flatMap(col =>
        col.subColumns.map(() => col.label)
      ).join("\t");
      headerRows.push(mainHeaderRow);
      // Only include sub headers when not hidden
      if (!hideSubHeaderRow) {
        const subHeaderRow = columns.flatMap(col =>
          col.subColumns.map(subCol => subCol.label)
        ).join("\t");
        headerRows.push(subHeaderRow);
      }
      
      // Build data rows
      const dataRows = rows.map((row, rowIndex) => {
        return columns.flatMap(col => 
          col.subColumns.map(subCol => {
            if (subCol.field) {
              // Match the exact same logic as TableCell component
              const rawValue = getCellValue ? getCellValue(row, subCol.field, rowIndex) : row[subCol.field];
              
              // If getCellValue was provided and returned a string, ALWAYS use it directly to preserve spaces
              // This ensures we match exactly what's displayed in the UI
              let displayValue;
              if (getCellValue && typeof rawValue === "string") {
                // getCellValue returned a string - use it directly to preserve ALL spaces
                displayValue = rawValue;
              } else if (getCellValue && rawValue !== String(row[subCol.field])) {
                // getCellValue formatted it differently - use it directly
                displayValue = rawValue;
              } else {
                displayValue = subCol.format
                  ? subCol.format(rawValue)
                  : formatCellValue(rawValue, subCol.type, currencySymbol);
              }
              
              // Remove HTML tags if any, but preserve ALL whitespace
              // Don't use String() conversion as it might normalize spaces
              return typeof displayValue === "string" 
                ? displayValue.replace(/<[^>]*>/g, "")
                : String(displayValue);
            }
            return "";
          })
        ).join("\t");
      });
      
      // Combine headers and data
      return [...headerRows, ...dataRows].join("\n");
    };

    // Check if ClipboardItem is available (browser API)
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard && navigator.clipboard.write) {
      try {
        // Create a blob with HTML and plain text fallback
        const htmlBlob = new Blob([html], { type: 'text/html' });
        const plainText = buildPlainText();
        const textBlob = new Blob([plainText], { type: 'text/plain' });
        
        // Use Clipboard API with both HTML and plain text
        // eslint-disable-next-line no-undef
        const clipboardItem = new ClipboardItem({
          'text/html': htmlBlob,
          'text/plain': textBlob
        });
        
        await navigator.clipboard.write([clipboardItem]);
        return;
      } catch (clipboardErr) {
        // Fall through to plain text fallback
        console.warn("ClipboardItem API failed, falling back to plain text:", clipboardErr);
      }
    }
    
    // Fallback to plain text if ClipboardItem is not available
    const plainText = buildPlainText();
    await navigator.clipboard.writeText(plainText);
    
  } catch (err) {
    console.error("Failed to copy table data:", err);
    // Final fallback to plain text if everything fails
    try {
      // Build header row(s)
      const headerRows = [];
      // Always include main headers
      const mainHeaderRow = columns.flatMap(col =>
        col.subColumns.map(() => col.label)
      ).join("\t");
      headerRows.push(mainHeaderRow);
      // Only include sub headers when not hidden
      if (!hideSubHeaderRow) {
        const subHeaderRow = columns.flatMap(col =>
          col.subColumns.map(subCol => subCol.label)
        ).join("\t");
        headerRows.push(subHeaderRow);
      }
      
      // Build data rows
      const dataRows = rows.map((row, rowIndex) => {
        return columns.flatMap(col => 
          col.subColumns.map(subCol => {
            if (subCol.field) {
              // Match the exact same logic as TableCell component
              const rawValue = getCellValue ? getCellValue(row, subCol.field, rowIndex) : row[subCol.field];
              
              // If getCellValue was provided and returned a string, ALWAYS use it directly to preserve spaces
              // This ensures we match exactly what's displayed in the UI
              let displayValue;
              if (getCellValue && typeof rawValue === "string") {
                // getCellValue returned a string - use it directly to preserve ALL spaces
                displayValue = rawValue;
              } else if (getCellValue && rawValue !== String(row[subCol.field])) {
                // getCellValue formatted it differently - use it directly
                displayValue = rawValue;
              } else {
                displayValue = subCol.format
                  ? subCol.format(rawValue)
                  : formatCellValue(rawValue, subCol.type, currencySymbol);
              }
              
              // Remove HTML tags if any, but preserve ALL whitespace
              // Don't use String() conversion as it might normalize spaces
              return typeof displayValue === "string" 
                ? displayValue.replace(/<[^>]*>/g, "")
                : String(displayValue);
            }
            return "";
          })
        ).join("\t");
      });
      
      // Combine headers and data
      const plainText = [...headerRows, ...dataRows].join("\n");
      await navigator.clipboard.writeText(plainText);
    } catch (fallbackErr) {
      console.error("Fallback copy also failed:", fallbackErr);
      throw fallbackErr;
    }
  }
};
